import {DataFetcher, useGrpcRequest} from '@aboutyou/data-fetcher';
import {BrandHubPageMetaService_GetMetaDataLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/meta/service.grpc.lazy';
import {GetMetaDataResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/meta/service.types';
import {Head} from '@aboutyou/head-elements';
import {RouterErrorHandler, useRecentFullUrl} from '@aboutyou/router';
import {TrackContentPageView} from '@aboutyou/tracking';
import React, {Fragment, memo} from 'react';
import {useCategoryIdentifier} from '../../features/hooks/useCategoryIdentifier';
import {useAppliedPotentialFilters} from '../../features/ProductStream/utils';
import {useReductionsStateRequestParameter} from '../../features/ReductionsState/useReductionsStateRequestParameter';
import {
  ensure404Page,
  extractMetaAndLinkTags,
  isSeoData,
  setDataTestIds,
} from '../../shared-deprecated/metaUtils';
import {useEnsureRedirectData} from '../../shared-deprecated/useEnsureRedirectData';
import {useUpdateTargetGroup} from '../../shared-deprecated/useSelectedTargetGroup';
import {useTargetGroupIdentifier} from '../../shared-deprecated/useTargetGroupIdentifier';
import {useOffcanvasOpenState} from '../Navigation/useOffcanvasOpenState';
import {useBrandShopIdentifier} from './utils/useBrandShopIdentifier';

/**
 * Renders all meta information and handles basic tracking events of the Brand Shop Page. This component is also responsible for redirects and 404 handling.
 */
export const BrandShopMeta: React.FC = props => {
  return (
    <DataFetcher
      request={useMetaData()}
      Error={RouterErrorHandler}
      View={MemoView}
      viewProps={props}
    />
  );
};

const useMetaData = () => {
  const fullUrl = useRecentFullUrl();
  const brandIdentifier = useBrandShopIdentifier();
  const targetGroupIdentifier = useTargetGroupIdentifier();
  const categoryIdentifier = useCategoryIdentifier();
  const appliedFilters = useAppliedPotentialFilters();
  const ensureRedirectData = useEnsureRedirectData();
  const {isOpen: isOffCanvasOpen} = useOffcanvasOpenState();

  return useGrpcRequest(
    BrandHubPageMetaService_GetMetaDataLazy,
    {
      brand: {
        identifier: brandIdentifier,
      },
      category: categoryIdentifier || null,
      targetGroup: targetGroupIdentifier,
      appliedFilters: {
        filters: appliedFilters,
      },
      url: fullUrl,
      reductionsState: useReductionsStateRequestParameter(),
    },
    {
      keepPreviousData: false,
      validateData: ensureRedirectData,
      validateError: ensure404Page,
      // switching the gender in the off-canvas would otherwise trigger this request that would return a different target group
      // that will switch back the gender, resulting in a UI "glitch"
      disabled: isOffCanvasOpen,
    },
  );
};

const View: React.FC<GetMetaDataResponse> = ({
  meta,
  recommendedTargetGroup,
}) => {
  const {isTargetGroupUpdating} = useUpdateTargetGroup(recommendedTargetGroup);

  if (!isSeoData(meta)) {
    return null;
  }

  const seoData = meta.type.seoData;
  const [metaTags, linkTags] = setDataTestIds(
    extractMetaAndLinkTags(seoData.elements),
  );

  return (
    <Fragment>
      {!isTargetGroupUpdating && (
        <TrackContentPageView pageTitle={seoData.title} />
      )}
      <Head meta={metaTags} link={linkTags}>
        <title data-testid="MetaTitle">{seoData.title}</title>
      </Head>
    </Fragment>
  );
};

const MemoView = memo(View);
